import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { saveAs } from 'file-saver';
import TableWithPagination from '../../../components/TableWithPagination';
import { useLoanContext } from '../LoanContext';
import { formatAmount, formatDate } from '../../../utils/formatters';
import { formatDateToDDMMYYYY } from '../../../utils/dateUtils';
import PrimaryButton from '../../../components/styled/PrimaryButton';

type ScheduleRow = {
  dueDate: string;
  dueAmount: number;
  duePrincipal: number;
  dueInterest: number;
  balance: number;
  paymentStatus: string;
  interestRate: number;
};

const FooterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const formatInterestRate = (val: unknown): string => {
  if (val === undefined || val === null || val === '') {
    return '—';
  }
  const numValue = parseFloat(String(val));
  if (!isNaN(numValue)) {
    return `${numValue.toFixed(2)}%`;
  }
  return '—';
};

const ScheduleTab = () => {
  const { loan } = useLoanContext();
  const loanId = useParams().loanId;
  const schedule: ScheduleRow[] = (loan?.schedule as ScheduleRow[]) || [];

  /*
   * Preserved old dialog/excel flow for future use (intentionally commented out):
   *
   * const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
   * const [pendingDownloadFormat, setPendingDownloadFormat] = useState<'PDF' | 'Excel'>('PDF');
   *
   * if (format === 'Excel') {
   *   // Excel workbook generation branch
   *   // ...
   * } else {
   *   // PDF download branch
   * }
   */
  const [bannerError, setBannerError] = useState<string>('');

  const handleDownload = async () => {
    setBannerError('');

    const accountNumber = loan?.name || loanId || 'Unknown';
    const todayDate = formatDateToDDMMYYYY(new Date());
    const baseFileName = `${accountNumber} - Amortisation Schedule - ${todayDate}`;

    if (!loanId) {
      setBannerError('Loan Account Id is required to download PDF.');
      return;
    }

    try {
      const endpoint = `/api/loans/${encodeURIComponent(loanId)}/amortizationpdfrestapi`;
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        withCredentials: true,
        validateStatus: () => true,
      });
      const contentType = String(response.headers?.['content-type'] || '');

      if (response.status >= 400 || !contentType.toLowerCase().includes('application/pdf')) {
        throw new Error('Expected PDF response from middleware endpoint.');
      }

      const blob = response.data;
      saveAs(blob, `${baseFileName}.pdf`);
    } catch {
      setBannerError('Failed to generate PDF file. Please try again or contact support.');
    }
  };

  return (
    <>
      <Box sx={{ width: '100%', overflowX: 'auto', borderRadius: 2 }}>
        <TableWithPagination
          columns={[
            { label: 'Due Date', key: 'dueDate', sortable: true, renderCell: (row) => row.dueDate ? formatDate(String(row.dueDate)) : '' },
            { label: 'Due Principal', key: 'duePrincipal', sortable: false, renderCell: (row) => formatAmount(row.duePrincipal) },
            { label: 'Due Interest', key: 'dueInterest', sortable: false, renderCell: (row) => formatAmount(row.dueInterest) },
            { label: 'Due Amount', key: 'dueAmount', sortable: false, renderCell: (row) => formatAmount(row.dueAmount) },
            { label: 'Balance', key: 'balance', sortable: false, renderCell: (row) => formatAmount(row.balance) },
            // { label: 'Payment Status', key: 'paymentStatus', sortable: true, renderCell: (row) => (row.paymentStatus != null ? String(row.paymentStatus) : '—') },
            { label: 'Interest Rate (%)', key: 'interestRate', sortable: false, renderCell: (row) => formatInterestRate(row.interestRate) },
          ]}
          rows={schedule}
          getRowKey={(row) => String(row.dueDate ?? '') + '-' + String(row.dueAmount ?? '')}
          rowsPerPage={20}
        />
      </Box>

      <FooterContainer>
        <PrimaryButton
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={schedule.length === 0}
          sx={{
            width: '469px',
          }}
        >
          Download
        </PrimaryButton>
      </FooterContainer>
      {/*
        Preserved old popup UI for format selection (intentionally commented out):

        <Dialog open={downloadDialogOpen} ...>
          <DialogTitle>Download</DialogTitle>
          <DialogContent>
            <RadioGroup value={pendingDownloadFormat} ...>
              <FormControlLabel value="PDF" ... />
              <FormControlLabel value="Excel" ... />
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <SecondaryButton ...>Cancel</SecondaryButton>
            <PrimaryButton ...>Download</PrimaryButton>
          </DialogActions>
        </Dialog>
      */}
      {bannerError && (
        <Typography variant="body2" sx={{ color: '#f44336', mt: 1, textAlign: 'center' }}>
          {bannerError}
        </Typography>
      )}
    </>
  );
};

export default ScheduleTab;